import {
  EGRNLotListingModuleType,
  EGRNModuleTypeForPayload,
  TCardInfoDataTypes,
  TCardInfoTypes,
  TDetailedCardInfoDTO,
  TDetailedInfoForMaterialRequestOrderDTO,
  TFabricLotListingMetadata,
  TOrganizi8ZfTabs,
  TPaginationInfo,
  TSubRouteStatusTabDetails,
  TTrimItemDTO,
  TUserPaginationOption,
  TZfTabsDTO,
} from '@monorepo/types';

type KeyMap<T extends readonly string[]> = {
  keysOrder: T;
  keysToDisplayName: Record<T[number], string>;
};

const currentPathToBackPathMap: Record<string, string> = {
  '/create-grn/FABRIC': '/landing',
  '/create-grn/TRIMS': '/landing',
  '/create-grn/FABRIC/lot-details/:lotId': '/create-grn/FABRIC',
  '/create-grn/TRIMS/lot-details/:lotId': '/create-grn/TRIMS',
  '/storage/FABRIC': '/landing',
  '/storage/TRIMS': '/landing',
  '/storage/FABRIC/grn-details/:lotId': '/storage/FABRIC',
  '/storage/TRIMS/grn-details/:lotId': '/storage/TRIMS',
  '/storage/FABRIC/warehouse-details': '/storage/FABRIC',
  '/storage/TRIMS/warehouse-details': '/storage/TRIMS',
  '/material-requests/PENDING': '/landing',
  '/material-requests/COMPLETED': '/landing',
  '/material-requests/PENDING/detailed-info': '/material-requests/PENDING',
  '/material-requests/COMPLETED/detailed-info': '/material-requests/COMPLETED',
  '/material-requests/PENDING/item-details': '/material-requests/PENDING/detailed-info',
  '/material-requests/COMPLETED/item-details': '/material-requests/COMPLETED/detailed-info',
};

export const Organizi8Helper = {
  getBackPath(currentPath: string): string {
    for (const path of Object.keys(currentPathToBackPathMap)) {
      // This regex tests path (with path params) like /lot-details/:id with /lot-details/123
      const regex = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$');
      if (regex.test(currentPath)) {
        return currentPathToBackPathMap[path];
      }
    }
    return '';
  },

  getDefaultPaginationInfo(): TPaginationInfo {
    const defaultPaginationInfo: TPaginationInfo = {
      rangeInPage: {
        start: 0,
        end: 0,
      },
      totalItems: 0,
      totalPageCount: 0,
      itemsPerPage: 10,
    };
    return defaultPaginationInfo;
  },

  getDefaultPaginationOption(): TUserPaginationOption {
    const paginationOption: TUserPaginationOption = {
      pageNo: 1,
      pageSize: 10,
    };
    return paginationOption;
  },

  getModuleTypeForGrnListingPayload(moduleType: EGRNLotListingModuleType) {
    return moduleType === EGRNLotListingModuleType.CREATE_GRN
      ? EGRNModuleTypeForPayload.CREATE_GRN
      : EGRNModuleTypeForPayload.STORAGE;
  },

  getZfTabsData() {
    const zfTabs: TZfTabsDTO[] = [
      {
        key: 'FABRIC',
        displayName: 'organizi8_helper.fabric',
      },
      {
        key: 'TRIMS',
        displayName: 'organizi8_helper.trims',
      },
    ];
    return zfTabs;
  },

  getSubRoutesItemTabs() {
    const tabs: TSubRouteStatusTabDetails = [
      {
        displayName: 'organizi8_helper.fabric',
        status: 'FABRIC',
      },
      {
        displayName: 'organizi8_helper.trims',
        status: 'TRIMS',
      },
    ];
    return tabs;
  },

  getTabDetailsForMaterialRequest() {
    const tabs: TSubRouteStatusTabDetails = [
      {
        displayName: 'organizi8_helper.pending',
        status: 'PENDING',
      },
      {
        displayName: 'organizi8_helper.completed',
        status: 'COMPLETED',
      },
    ];
    return tabs;
  },

  getZfTabsForMaterialRequest() {
    const tabs: TOrganizi8ZfTabs = [
      {
        key: 'FABRIC',
        displayName: 'organizi8_helper.fabric',
      },
      {
        key: 'TRIMS',
        displayName: 'organizi8_helper.trims',
      },
    ];
    return tabs;
  },

  // Will be updated in Phase 2 (Transfer Module Integration)
  getDisabledTabKeys(data: { FABRIC: any[]; TRIMS: any[] }) {
    const disabledTabKeys: string[] = [];
    Object.keys(data).map((key) => {
      if (data[key as 'FABRIC' | 'TRIMS'].length === 0) disabledTabKeys.push(key);
    });
    return disabledTabKeys;
  },

  validateKeyMap<T extends readonly string[]>(cardDataObject: KeyMap<T>): KeyMap<T> {
    return cardDataObject;
  },

  getKeysOrderAndKeysToDisplayNameMapForCardData(type: TCardInfoTypes): KeyMap<readonly string[]> {
    switch (type) {
      case 'FABRIC_GRN_LOT_DETAILS': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['supplierName', 'supplierPo', 'lotId'] as const,
          keysToDisplayName: {
            supplierName: 'organizi8_table_models.supplier_name',
            supplierPo: 'organizi8_table_models.supplier_po',
            lotId: 'organizi8_table_models.lot_id',
          },
        });
      }
      case 'TRIMS_GRN_LOT_DETAILS': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['supplier_name', 'supplier_po', 'lotId'] as const,
          keysToDisplayName: {
            supplier_name: 'organizi8_table_models.supplier_name',
            supplier_po: 'organizi8_table_models.supplier_po',
            lotId: 'organizi8_table_models.lot_id',
          },
        });
      }
      case 'MATERIAL_REQUESTS_DETAILED_INFO': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['materialRequestId', 'buyer', 'style', 'po'] as const,
          keysToDisplayName: {
            materialRequestId: 'organizi8_table_models.request_no',
            buyer: 'organizi8_table_models.buyer',
            style: 'organizi8_table_models.style',
            po: 'organizi8_table_models.po',
          },
        });
      }
      case 'MATERIAL_REQUESTS_ITEM_DETAILS': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['itemCode', 'itemName'] as const,
          keysToDisplayName: {
            itemCode: 'organizi8_table_models.item_id',
            itemName: 'organizi8_table_models.item_name',
          },
        });
      }
      case 'TRANSFER_LOCATION_INFO': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['bay', 'shelf', 'bin'] as const,
          keysToDisplayName: {
            bay: 'organizi8_helper.bay',
            shelf: 'organizi8_helper.shelf',
            bin: 'organizi8_helper.bin',
          },
        });
      }
      case 'TRANSFER_TO_LOCATION_INFO': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['locationId', 'bay', 'shelf', 'bin'] as const,
          keysToDisplayName: {
            locationId: 'organizi8_helper.warehouse',
            bay: 'organizi8_helper.bay',
            shelf: 'organizi8_helper.shelf',
            bin: 'organizi8_helper.bin',
          },
        });
      }
      case 'SELECTED_GRN_DETAILS': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['grnNumber', 'itemName', 'itemCode'] as const,
          keysToDisplayName: {
            grnNumber: 'organizi8_table_models.GRN_NO',
            itemName: 'organizi8_table_models.item_name',
            itemCode: 'organizi8_table_models.item_id',
          },
        });
      }
      case 'WAREHOUSE_DETAILS': {
        return Organizi8Helper.validateKeyMap({
          keysOrder: ['warehouse', 'bay', 'shelf', 'bin'] as const,
          keysToDisplayName: {
            warehouse: 'organizi8_helper.warehouse',
            bay: 'organizi8_helper.bay',
            shelf: 'organizi8_helper.shelf',
            bin: 'organizi8_helper.bin',
          },
        });
      }
    }
  },

  /**
   *
   * @param type Card Type
   * @returns Object for CardInfo reusable component
   * @description depends on keysOrder (these are API response object keys - different for each API) - returns default data as well for card model
   */
  getBriefDetailsCardData(type: TCardInfoTypes, data?: TCardInfoDataTypes): TDetailedCardInfoDTO[] {
    const { keysOrder, keysToDisplayName } = Organizi8Helper.getKeysOrderAndKeysToDisplayNameMapForCardData(type);
    const cardInfo: TDetailedCardInfoDTO[] = [];
    keysOrder.forEach((key) => {
      if (data) {
        const item = Object.keys(data).find((dataKey) => dataKey === key);
        if (item) {
          cardInfo.push({ key: keysToDisplayName[key], value: data[item as keyof typeof data] });
        } else {
          cardInfo.push({ key: keysToDisplayName[key], value: '--' });
        }
      } else cardInfo.push({ key: keysToDisplayName[key], value: '--' });
    });
    return cardInfo;
  },

  getGRNStatusAccordionAdditionalBlockDetails(grnStatus: TFabricLotListingMetadata['lotGRNStatus']) {
    switch (grnStatus) {
      case 'COMPLETED': {
        return {
          displayName: 'organizi8_table_models.completed',
          backgroundColor: 'green',
          textColor: 'white',
        };
      }
      case 'NOT_STARTED': {
        return {
          displayName: 'organizi8_table_models.not_started',
          backgroundColor: '#7C7E8A',
          textColor: 'white',
        };
      }
      case 'STARTED': {
        return {
          displayName: 'organizi8_table_models.started',
          backgroundColor: 'lightgreen',
        };
      }
      default: {
        return {
          displayName: grnStatus,
        };
      }
    }
  },

  /**
   *
   * @param item TDetailedInfoForMaterialRequestOrderDTO
   * @returns Unique key comprising of itemCode,itemColor,locationId (ex - TID1,TC1,TLID1)
   */
  getAccordionKeyForMaterialRequestDetailedInfo(item: TDetailedInfoForMaterialRequestOrderDTO) {
    const { itemCode, itemColor, locationId } = item;
    return `${itemCode},${itemColor},${locationId}`;
  },

  /**
   *
   * @param uniqueItemId string
   * @returns ItemCode from itemCode,itemColor,locationId (itemCode is at index 0)
   */
  getItemCodeFromUniqueAccordionIdInMaterialRequestDetailedInfo(uniqueItemId: string) {
    return uniqueItemId.split(',')[0];
  },

  /**
   *
   * @param item TTrimItemDTO
   * @returns Unique key comprising of size,locationId (ex - XL,LID1)
   */
  getAccordionKeyForTrimsItemDetailsInMaterialRequest(item: TTrimItemDTO) {
    const { size, locationId } = item;
    return `${size},${locationId}`;
  },
};
